import axios from "axios";
import { outboxAdd, outboxAll, outboxRemove, outboxCount } from "@/lib/offlineDb";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("pms_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  const pid = localStorage.getItem("pms_active_property");
  if (pid && !cfg.headers["X-Property-Id"]) cfg.headers["X-Property-Id"] = pid;
  return cfg;
});

// ---- Human-readable error surface ----
// The interceptor auto-toasts errors that pages haven't explicitly handled.
// A caller can opt-out by setting `config.silent = true` (used for background
// fetches / auth probes) or by setting `config.__handled = true` in a catch.
const humanize = (err) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    // FastAPI validation errors — pull first msg + loc
    const first = detail[0] || {};
    const loc = Array.isArray(first.loc) ? first.loc.filter((l) => l !== "body").join(".") : "";
    return loc ? `${loc}: ${first.msg}` : first.msg || "Validation failed";
  }
  if (typeof detail === "string") return detail;
  if (typeof err?.response?.data === "string") return err.response.data;
  if (err?.message === "Network Error") return "Network error — you may be offline. Changes are queued for sync.";
  if (err?.code === "ECONNABORTED") return "Server took too long. Please retry.";
  if (status === 400) return "Bad request — check the details you entered.";
  if (status === 401) return "Session expired. Please sign in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "Not found.";
  if (status === 409) return err?.response?.data?.detail || "Conflict — please refresh and try again.";
  if (status === 413) return "File too large.";
  if (status === 422) return "Some fields didn't validate — please review.";
  if (status && status >= 500) return "Server error. Our team has been notified — please retry.";
  return err?.message || "Something went wrong.";
};

// Auth 401 handler + toast
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const cfg = err?.config || {};
    if (err?.response?.status === 401) {
      const p = window.location.pathname;
      if (p !== "/login" && p !== "/auth/callback") {
        localStorage.removeItem("pms_token");
        window.location.href = "/login";
      }
    }
    // Auto-toast unless the caller marked the request silent or handled it locally
    const method = (cfg.method || "get").toLowerCase();
    if (!cfg.silent && !cfg.__handled && !cfg.__offlineQueued) {
      // Skip toasts for background GETs while offline — that's expected
      const online = typeof navigator === "undefined" || navigator.onLine;
      if (!(method === "get" && !online)) {
        try { toast.error(humanize(err)); } catch (e) { /* toast provider not ready */ }
      }
    }
    return Promise.reject(err);
  }
);

/* -----------------------------------------------------------
 * Offline mutation queue
 * When navigator.onLine is false, non-GET requests are queued in
 * IndexedDB. When we come back online (or on load) the queue is
 * flushed serially. Reads that fail fall back to the service worker
 * cache — that's handled in /public/sw.js.
 * ----------------------------------------------------------- */
const QUEUEABLE_METHODS = ["post", "put", "patch", "delete"];

const emitQueueEvent = (size) => {
  window.dispatchEvent(new CustomEvent("pms:queue", { detail: { size } }));
};

// Intercept mutations while offline
api.interceptors.request.use(async (cfg) => {
  const method = (cfg.method || "get").toLowerCase();
  if (!navigator.onLine && QUEUEABLE_METHODS.includes(method)) {
    await outboxAdd({
      method,
      url: cfg.url,
      data: cfg.data,
      params: cfg.params,
      // Skip auth header — will be re-added by request interceptor on flush
    });
    const size = await outboxCount();
    emitQueueEvent(size);
    // Short-circuit: return a synthetic response so the UI doesn't error
    // eslint-disable-next-line no-throw-literal
    throw {
      __offlineQueued: true,
      config: cfg,
      response: { data: { ok: true, queued: true, queued_at: new Date().toISOString() }, status: 202 },
      message: "Change queued — will sync when back online",
    };
  }
  return cfg;
});

// Convert our synthetic offline-queued object into a normal-looking success
api.interceptors.response.use(undefined, (err) => {
  if (err && err.__offlineQueued) {
    return Promise.resolve(err.response);
  }
  return Promise.reject(err);
});

export const getOfflineQueueSize = () => {
  // Non-async convenience: returns 0 immediately, event will update UI later
  let count = 0;
  outboxCount().then((c) => { count = c; emitQueueEvent(c); }).catch(() => {});
  return count;
};

export const outboxSizeAsync = () => outboxCount();

let flushing = false;

// Warm-up: after login, pre-fetch key endpoints so they're available offline.
// Runs asynchronously — swallows errors.
export const warmOfflineCache = async (properties = []) => {
  const paths = [
    "/settings/platform",
    "/settings/platform/public",
    "/auth/permissions",
    "/permissions/defaults",
    "/channels",
    "/properties",
    "/guests",
    "/quotations",
    "/reservations",
  ];
  // Property-scoped endpoints
  for (const p of properties.slice(0, 5)) {
    paths.push(`/rooms?property_id=${p.id}`);
    paths.push(`/halls?property_id=${p.id}`);
    paths.push(`/amenities?property_id=${p.id}`);
    paths.push(`/pos/menu?property_id=${p.id}`);
    paths.push(`/pos/stock-items?property_id=${p.id}`);
    paths.push(`/housekeeping?property_id=${p.id}`);
    paths.push(`/invoices?property_id=${p.id}`);
  }
  await Promise.all(paths.map((p) => api.get(p, { silent: true }).catch(() => {})));
};

export const flushOfflineQueue = async () => {
  if (flushing) return;
  if (!navigator.onLine) return;
  flushing = true;
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const items = await outboxAll();
      if (items.length === 0) break;
      const item = items[0];
      try {
        await api.request({
          method: item.method,
          url: item.url,
          data: item.data,
          params: item.params,
        });
        await outboxRemove(item.id);
        const size = await outboxCount();
        emitQueueEvent(size);
      } catch (e) {
        // If server rejects (e.g. validation), drop the item to avoid infinite retry
        if (e?.response && e.response.status >= 400 && e.response.status < 500) {
          await outboxRemove(item.id);
          const size = await outboxCount();
          emitQueueEvent(size);
          continue;
        }
        // Network/5xx error — stop and retry on next flush
        break;
      }
    }
  } finally {
    flushing = false;
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("online", () => { flushOfflineQueue(); });
  setTimeout(flushOfflineQueue, 1500);
  // Emit initial count for the badge
  outboxCount().then((c) => emitQueueEvent(c)).catch(() => {});
}
